import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { uploadIdentityDocument } from '../../api/api';
import { useAuthStore } from '../../stores/useAuthStore';

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];

const UploadIdentityDocumentModal = ({ isOpen, onClose }) => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const setCurrentUser = useAuthStore((state) => state.setCurrentUser);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const documents = useMemo(() => currentUser?.identityDocuments || [], [currentUser]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error('Formato no valido. Solo JPG, PNG o PDF.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`El archivo excede ${MAX_FILE_SIZE_MB}MB.`);
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo para subir.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const result = await uploadIdentityDocument(selectedFile, (progressEvent) => {
      const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      setUploadProgress(percent);
    });

    if (result?.user) {
      setCurrentUser(result.user);
      toast.success(result.message || 'Documento enviado para verificacion.');
      setSelectedFile(null);
    }

    setIsUploading(false);
    setUploadProgress(0);
  };

  if (!isOpen) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Verificacion de Identidad (KYC)</h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          aria-label="Cerrar"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>

      <p className="text-xs text-gray-600 dark:text-gray-300 mb-4">
        Sube una imagen de tu documento (JPG, PNG) o un PDF. Maximo {MAX_FILE_SIZE_MB}MB. Tu informacion sera verificada por nuestro equipo.
      </p>

      <div className="space-y-3">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          className="block w-full text-xs text-gray-700 dark:text-gray-200"
          disabled={isUploading}
        />

        {isUploading && (
          <div className="w-full">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{uploadProgress}%</p>
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-blue-300"
        >
          {isUploading ? 'Subiendo...' : 'Enviar documento'}
        </button>
      </div>

      <div className="mt-6">
        <h3 className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">
          Historial de documentos
        </h3>
        {documents.length === 0 ? (
          <p className="text-xs text-gray-500">Aun no has subido documentos.</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.publicId || doc.url} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                <div className="flex items-center space-x-2">
                  <i className="fas fa-file-alt text-blue-500"></i>
                  <div>
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{doc.name}</p>
                    <p className="text-[10px] text-gray-500">Estado: {doc.status || 'pending'}</p>
                  </div>
                </div>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline"
                >
                  Ver
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadIdentityDocumentModal;
